#!/usr/bin/env node
/* oxlint-disable no-console */

/**
 * Stonecrop Schema CLI
 *
 * Converts GraphQL introspection results to Stonecrop doctype JSON schemas.
 *
 * Usage:
 *   stonecrop-schema generate --endpoint <url> --output <dir>
 *   stonecrop-schema generate --introspection <file.json> --output <dir>
 *   stonecrop-schema generate --sdl <file.graphql> --output <dir>
 *
 * @packageDocumentation
 */

import { readFileSync, writeFileSync, mkdirSync, existsSync } from 'node:fs'
import { resolve, join } from 'node:path'
import { parseArgs } from 'node:util'
import { getIntrospectionQuery, type IntrospectionQuery } from 'graphql'

import { authoredPrimaryKey } from './converter/authored'
import { convertGraphQLSchema, formatDoctypeDrift, mergeIntrospectedDoctype, planGeneration } from './converter/index'
import { stripFieldKind } from './field'
import { validateDoctype } from './validation'
import type { GraphQLConversionOptions } from './converter/types'

/**
 * Fetch an introspection result from a live GraphQL endpoint.
 *
 * @param endpoint - The GraphQL endpoint URL
 * @param headers - Optional HTTP headers
 * @returns The introspection query result
 */
async function fetchIntrospection(endpoint: string, headers?: Record<string, string>): Promise<IntrospectionQuery> {
	const response = await fetch(endpoint, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			...headers,
		},
		body: JSON.stringify({
			query: getIntrospectionQuery(),
		}),
	})

	if (!response.ok) {
		throw new Error(`Failed to fetch introspection: ${response.status} ${response.statusText}`)
	}

	const json: { data?: IntrospectionQuery; errors?: Array<{ message: string }> } = await response.json()

	if (json.errors?.length) {
		throw new Error(`GraphQL errors: ${json.errors.map(e => e.message).join(', ')}`)
	}

	if (!json.data) {
		throw new Error('No data in introspection response')
	}

	return json.data
}

async function main(): Promise<void> {
	const { values, positionals } = parseArgs({
		allowPositionals: true,
		options: {
			endpoint: { type: 'string', short: 'e' },
			introspection: { type: 'string', short: 'i' },
			sdl: { type: 'string', short: 's' },
			output: { type: 'string', short: 'o' },
			include: { type: 'string' },
			exclude: { type: 'string' },
			names: { type: 'string' },
			'custom-scalars': { type: 'string' },
			'include-unmapped': { type: 'boolean', default: false },
			'no-aggregates': { type: 'boolean', default: false },
			check: { type: 'boolean', default: false },
			help: { type: 'boolean', short: 'h' },
		},
	})

	const command = positionals[0]

	if (values.help || !command) {
		printHelp()
		process.exit(command ? 0 : 1)
	}

	if (command !== 'generate') {
		console.error(`Unknown command: ${command}`)
		console.error('Available commands: generate')
		process.exit(1)
	}

	// Determine source
	const sourceCount = [values.endpoint, values.introspection, values.sdl].filter(Boolean).length
	if (sourceCount !== 1) {
		console.error('Exactly one of --endpoint, --introspection, or --sdl must be provided')
		process.exit(1)
	}

	if (!values.output) {
		console.error('--output <dir> is required')
		process.exit(1)
	}

	const outputDir = resolve(values.output)

	// Build conversion options
	const options: GraphQLConversionOptions = {
		includeUnmappedMeta: values['include-unmapped'],
	}

	if (values.include) {
		options.include = values.include.split(',').map(s => s.trim())
	}

	if (values.exclude) {
		options.exclude = values.exclude.split(',').map(s => s.trim())
	}

	if (values.names) {
		const namesPath = resolve(values.names)
		options.doctypeNames = JSON.parse(readFileSync(namesPath, 'utf-8'))
	}

	options.onWarning = message => console.warn(`  WARN: ${message}`)

	if (values['custom-scalars']) {
		const scalarsPath = resolve(values['custom-scalars'])
		const scalarsContent = readFileSync(scalarsPath, 'utf-8')
		options.customScalars = JSON.parse(scalarsContent)
	}

	// Resolve source
	let source: IntrospectionQuery | string

	if (values.endpoint) {
		console.log(`Fetching introspection from ${values.endpoint}...`)
		source = await fetchIntrospection(values.endpoint)
	} else if (values.introspection) {
		const filePath = resolve(values.introspection)
		const content = readFileSync(filePath, 'utf-8')
		const parsed = JSON.parse(content)
		// Handle both { data: { __schema: ... } } and { __schema: ... } formats
		source = parsed.data ?? parsed
	} else {
		const filePath = resolve(values.sdl!)
		source = readFileSync(filePath, 'utf-8')
	}

	// Convert
	const entities = convertGraphQLSchema(source, options)

	if (entities.length === 0) {
		console.warn('No entity types found in the schema. Check your include/exclude filters.')
		process.exit(0)
	}

	// Read the identity each authored doctype declares, before planning rather than during the write
	// loop below. SDL cannot express which UNIQUE column is a table's key, so for a natural-key
	// doctype the declaration in the file is the only answer that exists — and planning is what
	// decides whether the aggregate can be built at all. Reading it 20 lines later, as the loop
	// does, made the "declare a primaryKey and re-run" warning a dead end: re-running after
	// declaring one changed nothing.
	const identity: Record<string, string> = {}
	for (const entity of entities) {
		const entityPath = join(outputDir, `${entity.slug}.json`)
		if (!existsSync(entityPath)) continue
		const declared = authoredPrimaryKey(JSON.parse(readFileSync(entityPath, 'utf-8')))
		if (declared !== undefined) identity[entity.name] = declared
	}

	// Each table yields two doctypes — the entity and its aggregate — written as peers, one file
	// each, along with what each is verified against. See `planGeneration`.
	const doctypes = planGeneration(entities, {
		noAggregates: values['no-aggregates'],
		identity,
		onWarning: message => console.warn(`  WARN: ${message}`),
	})

	// Write output
	if (!existsSync(outputDir)) {
		mkdirSync(outputDir, { recursive: true })
	}

	let warnings = 0
	let errors = 0
	let changed = 0
	const driftLines: string[] = []

	for (const { generated, basis, subset } of doctypes) {
		const fileName = `${generated.slug}.json`
		const filePath = join(outputDir, fileName)

		// When a doctype already exists it is the source of truth: generation confirms it and adds
		// provenance markers, and reports anything it disagrees with rather than applying it. A
		// doctype legitimately declares identity the schema cannot express — most often a natural
		// key that is a UNIQUE constraint, not the table's PRIMARY KEY — and overwriting that would
		// silently re-key the doctype on every run. A first generation has nothing to merge into,
		// so converter output is written verbatim.
		let output: object = generated
		if (existsSync(filePath)) {
			const { doctype: merged, drift } = mergeIntrospectedDoctype(JSON.parse(readFileSync(filePath, 'utf-8')), basis, {
				subset,
			})
			output = merged
			driftLines.push(...formatDoctypeDrift(drift))
		}

		// Serialize the merged object directly. Never round-trip it through the Zod parser first:
		// that runs in strip mode and would silently drop every key this package does not model,
		// `handler` on an action being the one consumers actually rely on.
		//
		// `kind` is dropped on the way out because it is the discriminated-union tag the parser
		// synthesizes, not authored data — `injectKind` puts it back on every read. Writing it made
		// the generator and the docbuilder disagree with hand-authored files about what a doctype
		// looks like, and re-added the key on every regeneration.
		const serializable: Record<string, unknown> = { ...output }
		if (Array.isArray(serializable.fields)) {
			serializable.fields = serializable.fields.map(stripFieldKind)
		}
		const json = JSON.stringify(serializable, null, '\t') + '\n'
		const unchanged = existsSync(filePath) && readFileSync(filePath, 'utf-8') === json
		if (!unchanged) changed++

		if (!values.check && !unchanged) {
			writeFileSync(filePath, json, 'utf-8')
		}

		// Validate the output
		const validation = validateDoctype(output)
		if (!validation.success) {
			errors++
			console.error(`  ERROR: ${fileName} failed validation:`)
			for (const err of validation.errors) {
				console.error(`    ${err.path.join('.')}: ${err.message}`)
			}
		} else {
			// Check for unmapped fields
			const unmappedFields = generated.fields.filter((f: any) => f._unmapped)
			if (unmappedFields.length > 0) {
				warnings++
				console.warn(
					`  WARN: ${fileName} has ${unmappedFields.length} unmapped field(s): ${unmappedFields
						.map((f: any) => f.fieldname)
						.join(', ')}`
				)
			}
		}
	}

	if (driftLines.length > 0) {
		console.log('\nDrift between the authored doctypes and the schema (reported, not applied):')
		for (const line of driftLines) console.log(line)
	}

	console.log(
		`\n${values.check ? 'Checked' : 'Generated'} ${doctypes.length} doctype(s) in ${outputDir}` +
			(changed ? ` (${changed} ${values.check ? 'would change' : 'written'})` : ' (all up to date)') +
			(warnings ? ` (${warnings} with warnings)` : '') +
			(errors ? ` (${errors} with errors)` : '')
	)

	if (errors > 0 || (values.check && changed > 0)) {
		process.exit(1)
	}
}

function printHelp(): void {
	console.log(`
stonecrop-schema - Convert GraphQL schemas to Stonecrop doctypes

USAGE:
  stonecrop-schema generate [options]

SOURCE (exactly one required):
  --endpoint, -e <url>         Fetch introspection from a live GraphQL endpoint
  --introspection, -i <file>   Read from a saved introspection JSON file
  --sdl, -s <file>             Read from a GraphQL SDL (.graphql) file

OUTPUT:
  --output, -o <dir>           Directory to write doctype JSON files (required)

OPTIONS:
  --include <types>            Comma-separated list of type names to include
  --exclude <types>            Comma-separated list of type names to exclude
  --names <file>               JSON file mapping GraphQL type name to doctype name
  --custom-scalars <file>      JSON file mapping custom scalar names to field templates
  --include-unmapped           Include _graphqlType metadata on unmapped fields
  --no-aggregates              Emit only the entity doctype, not its aggregate
  --check                      Report drift and exit non-zero if anything would change; write nothing
  --help, -h                   Show this help message

Each table generates TWO doctypes: the entity (every column, backs the record form) and its
aggregate (the collection view, identity column only by default), named as simple plurals —
'task.json' and 'tasks.json'. Widen an aggregate by adding fields to it — curation survives
regeneration. A doctype whose name is already plural gets no aggregate, because it would claim
its own name and its own file; the run says so and still writes the entity.

An aggregate needs an identity column. A table keyed on 'id' gives one up; for a natural key,
declare 'primaryKey' on the field in the entity's own file and re-run — the aggregate is keyed
on whatever that file declares.

NOTE: an existing doctype file is the source of truth. Regeneration verifies it against the
schema and adds 'source: introspected' markers; it reports disagreements rather than
overwriting them, so hand-curation survives. Use --check in CI.

EXAMPLES:
  # From a live PostGraphile server
  stonecrop-schema generate -e http://localhost:5000/graphql -o ./schemas

  # From a saved introspection result
  stonecrop-schema generate -i introspection.json -o ./schemas

  # From an SDL file with custom scalars
  stonecrop-schema generate -s schema.graphql -o ./schemas \\
    --custom-scalars custom-scalars.json

  # Only convert specific types
  stonecrop-schema generate -e http://localhost:5000/graphql -o ./schemas \\
    --include "User,Post,Comment"
`)
}

main().catch(err => {
	console.error('Error:', err.message)
	process.exit(1)
})
